import React, { useEffect, useState } from 'react';
import { Train, TramFront, Coffee, ShoppingBag, Car, MapPin, Footprints } from 'lucide-react';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { obfuscatedCoordinates } from '../../util/maps';
import { fetchNearbyPois, fetchWalkingRoute } from '../../util/mapboxPoi';
import AirbnbStyleMap from './AirbnbStyleMap';

import css from './SectionLocationFullBleed.module.css';

const BUCKET_ICONS = {
  train: Train,
  transit: TramFront,
  food: Coffee,
  shopping: ShoppingBag,
  parking: Car,
};

const BUCKET_LABEL_IDS = {
  train: 'ListingPage.poi.train',
  transit: 'ListingPage.poi.transit',
  food: 'ListingPage.poi.food',
  shopping: 'ListingPage.poi.shopping',
  parking: 'ListingPage.poi.parking',
};

const PoiRow = ({ poi }) => {
  const Icon = BUCKET_ICONS[poi.bucket] || MapPin;
  return (
    <li className={css.poiRow}>
      <span className={css.poiIcon} aria-hidden="true">
        <Icon size={20} strokeWidth={1.75} />
      </span>
      <div className={css.poiText}>
        <span className={css.poiCategory}>
          <FormattedMessage id={BUCKET_LABEL_IDS[poi.bucket]} />
        </span>
        {poi.name ? <span className={css.poiName}> · {poi.name}</span> : null}
      </div>
      <span className={css.poiWalk}>
        <Footprints size={14} strokeWidth={2} aria-hidden="true" />
        <FormattedMessage id="ListingPage.poi.walkMinutes" values={{ minutes: poi.minutes }} />
      </span>
    </li>
  );
};

/**
 * Uber-style ETA chip pinned to the bottom-left of the map. Highlights
 * the walking time to the nearest rail station; falls back to the
 * closest bus/transit stop when no rail station is within range.
 */
const TrainEtaChip = ({ stop }) => {
  if (!stop) return null;
  const isRail = stop.bucket === 'train';
  const Icon = isRail ? Train : TramFront;
  const etaId = isRail ? 'ListingPage.poi.trainEtaMinutes' : 'ListingPage.poi.transitEtaMinutes';
  const fallbackLabelId = isRail ? 'ListingPage.poi.train' : 'ListingPage.poi.transit';
  return (
    <div className={css.trainChip} role="status">
      <span className={css.trainChipIcon} aria-hidden="true">
        <Icon size={22} strokeWidth={2} />
      </span>
      <div className={css.trainChipBody}>
        <span className={css.trainChipEta}>
          <FormattedMessage id={etaId} values={{ minutes: stop.minutes }} />
        </span>
        <span className={css.trainChipName}>
          {stop.name || <FormattedMessage id={fallbackLabelId} />}
        </span>
      </div>
    </div>
  );
};

/**
 * Centered Airbnb-style map section with an Uber-inspired train-ETA
 * chip pinned on the map and a list of the nearest POIs underneath.
 * Lives inside the main info column so it aligns with the other
 * listing-detail sections (description, features, reviews, etc.).
 */
const SectionLocationFullBleed = props => {
  const intl = useIntl();
  const config = useConfiguration();
  const { geolocation, publicData, listingId, mapsConfig: mapsConfigProp } = props;
  const mapsConfig = config?.maps || mapsConfigProp;

  // Starts empty in the server render and in the first client render alike; the POIs only ever
  // arrive from the browser-side fetch below.
  const [pois, setPois] = useState([]);
  const [route, setRoute] = useState(null);

  useEffect(() => {
    if (!geolocation) return undefined;
    const token = mapsConfig?.mapboxAccessToken;
    if (!token) return undefined;
    const ctrl = new AbortController();
    fetchNearbyPois(geolocation, token, { signal: ctrl.signal, radius: 1500 })
      .then(result => setPois(result))
      .catch(() => setPois([]));
    return () => ctrl.abort();
  }, [geolocation?.lat, geolocation?.lng, mapsConfig?.mapboxAccessToken]);

  // Once we know which stop the chip is using, fetch a walking route from the
  // listing point to it for the animated path overlay.
  const trainPoi = pois.find(p => p.bucket === 'train') || null;
  const transitPoi = pois.find(p => p.bucket === 'transit') || null;
  const chipPoi = trainPoi || transitPoi;
  const destLng = chipPoi?.lng;
  const destLat = chipPoi?.lat;
  const cacheKey = listingId ? `${listingId.uuid}_${geolocation?.lat}_${geolocation?.lng}` : null;
  const obfuscated =
    geolocation && mapsConfig?.fuzzy?.enabled
      ? obfuscatedCoordinates(geolocation, mapsConfig.fuzzy.offset, cacheKey)
      : null;
  const startPoint = obfuscated || geolocation;

  useEffect(() => {
    setRoute(null);
    if (!chipPoi || destLng == null || destLat == null || !startPoint) return undefined;
    const token = mapsConfig?.mapboxAccessToken;
    if (!token) return undefined;
    const ctrl = new AbortController();
    fetchWalkingRoute([startPoint.lng, startPoint.lat], [destLng, destLat], token, {
      signal: ctrl.signal,
    })
      .then(r => setRoute(r))
      .catch(() => setRoute(null));
    return () => ctrl.abort();
  }, [
    chipPoi?.bucket,
    destLng,
    destLat,
    startPoint?.lng,
    startPoint?.lat,
    mapsConfig?.mapboxAccessToken,
  ]);

  if (!geolocation) return null;

  const address = publicData?.location?.address || '';

  const ariaLabel = intl.formatMessage(
    { id: 'ListingPage.locationMapAriaLabel', defaultMessage: 'Map of {city}' },
    { city: address || '' }
  );

  // The chip's stop is hidden from the regular POI list to avoid duplication.
  const listPois = pois.filter(p => p !== chipPoi);

  return (
    <section className={css.root} id="listing-location-full">
      <h2 className={css.heading}>
        <FormattedMessage id="ListingPage.fullMapTitle" />
      </h2>

      <div className={css.mapWrapper} role="img" aria-label={ariaLabel}>
        <AirbnbStyleMap
          center={geolocation}
          obfuscatedCenter={obfuscated}
          mapsConfig={mapsConfig}
          accessToken={mapsConfig?.mapboxAccessToken}
          zoom={15}
          route={route}
          destination={
            chipPoi && destLng != null && destLat != null ? { lng: destLng, lat: destLat } : null
          }
        />
        <TrainEtaChip stop={chipPoi} />
      </div>

      {/* The "In der Nähe" block exists only when there is something to list. It used to print
          "Keine Punkte von Interesse … gefunden." while empty — and it is ALWAYS empty in the
          server render (the POIs are fetched in the browser) and in Googlebot's renderer
          (api.mapbox.com's robots.txt disallows the tilequery URL). Every listing page thus told
          Google "nothing found", a soft-404 trigger. Loading and empty now render nothing, which
          is also identical on the server and in the first client render (hydration). */}
      {listPois.length > 0 ? (
        <div className={css.poiWrapper}>
          <h3 className={css.poiHeading}>
            <FormattedMessage id="ListingPage.nearbyTitle" />
          </h3>
          <ul className={css.poiList}>
            {listPois.map(poi => (
              <PoiRow key={poi.bucket} poi={poi} />
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
};

export default SectionLocationFullBleed;
